import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Call() {
  const { id } = useParams();
  const { user } = useAuth();
  const [conv, setConv] = useState(null);
  const [timeLeft, setTimeLeft] = useState(600);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [stars, setStars] = useState(5);
  const [review, setReview] = useState("");
  const nav = useNavigate();
  const intervalRef = useRef(null);

  useEffect(()=>{ api.getConversation(id).then(setConv).catch(()=>{ toast.error("Not found"); nav("/"); }); }, [id]);

  const startCall = async () => {
    await api.startCall(id);
    setStarted(true);
    intervalRef.current = setInterval(()=> setTimeLeft(t => {
      if (t <= 1) { endCall(); return 0; }
      return t - 1;
    }), 1000);
  };

  const endCall = async () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setEnded(true);
    setStarted(false);
    try { await api.endCall(id); } catch {}
    if (user?.role === "student") setShowRating(true);
  };

  const submitRating = async () => {
    try { await api.submitRating({ conversation_id: id, stars, review_text: review }); toast.success("Thanks!"); nav("/dashboard/student"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  useEffect(()=>{ return () => intervalRef.current && clearInterval(intervalRef.current); }, []);

  if (!conv) return <div className="min-h-screen bg-[#0F172A] text-white flex items-center justify-center">Loading...</div>;
  const room = conv.jitsi_room_id || `campussenior-${id}`;
  const mm = String(Math.floor(timeLeft/60)).padStart(2,"0");
  const ss = String(timeLeft%60).padStart(2,"0");
  const warn = timeLeft <= 120 && timeLeft > 0;

  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      <div className="px-4 py-3 flex items-center justify-between border-b border-white/10">
        <button onClick={()=>nav(-1)} className="text-slate-400 hover:text-white flex items-center gap-2"><ArrowLeft size={18}/>Back</button>
        <div className="font-semibold">{conv.ambassador_name}</div>
        <div className={`font-display text-lg ${warn?"text-amber-400 animate-pulse":""}`}>{started?`${mm}:${ss}`:"10:00"}</div>
      </div>

      {warn && started && <div className="bg-amber-500 text-[#0F172A] text-center py-2 font-semibold text-sm">⚠️ Call ending in 2 minutes</div>}

      {!started && !ended && (
        <div className="max-w-md mx-auto px-6 py-12 text-center">
          <div className="glass rounded-2xl p-8">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#2563EB] to-[#1E3A8A] flex items-center justify-center text-2xl font-bold mb-4">{(conv.ambassador_name||"A").split(" ").map(w=>w[0]).slice(0,2).join("")}</div>
            <h2 className="font-display text-2xl font-bold">{conv.ambassador_name}</h2>
            <p className="text-slate-400 text-sm mt-1">{conv.ambassador_profile?.course} · {conv.ambassador_profile?.year} Year · {conv.college_name}</p>
            <p className="text-amber-400 text-xs mt-3">This session will last 10 minutes</p>
            <button data-testid="join-call" onClick={startCall} className="mt-6 w-full py-3 rounded-full bg-[#F59E0B] text-[#0F172A] font-bold">Join Call</button>
          </div>
        </div>
      )}

      {started && !ended && (
        <div className="w-full" style={{height:"calc(100vh - 110px)"}}>
          <iframe title="Jitsi" allow="camera; microphone; fullscreen; display-capture; autoplay" src={`https://meet.jit.si/${room}#config.prejoinPageEnabled=false&config.startWithAudioMuted=false&config.startWithVideoMuted=true&userInfo.displayName="${encodeURIComponent(user?.full_name||"User")}"`} className="w-full h-full border-0"/>
          <div className="fixed bottom-4 left-0 right-0 flex justify-center"><button data-testid="end-call" onClick={endCall} className="px-6 py-3 rounded-full bg-red-500 hover:bg-red-600 font-semibold">End Call</button></div>
        </div>
      )}

      {ended && (
        <div className="max-w-md mx-auto px-6 py-12 text-center">
          <motion.div initial={{scale:0}} animate={{scale:1}} transition={{type:"spring"}} className="w-20 h-20 mx-auto rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-4xl">✓</motion.div>
          <h2 className="font-display text-2xl font-bold mt-4">Session Complete</h2>
          <p className="text-slate-400 text-sm">Duration: {Math.floor((600-timeLeft)/60)}m {(600-timeLeft)%60}s</p>
        </div>
      )}

      {showRating && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="glass rounded-2xl p-6 max-w-md w-full">
            <h3 className="font-display text-xl font-bold mb-3">How was your call?</h3>
            <div className="flex justify-center gap-2 mb-4">{[1,2,3,4,5].map(n=><button key={n} onClick={()=>setStars(n)}><Star size={32} className={n<=stars?"fill-amber-400 text-amber-400":"text-slate-600"}/></button>)}</div>
            <textarea value={review} onChange={e=>setReview(e.target.value)} placeholder="Optional review..." className="w-full bg-[#1E293B] border border-white/10 rounded-lg p-3 mb-3"/>
            <button onClick={submitRating} className="w-full py-3 rounded-full bg-[#F59E0B] text-[#0F172A] font-bold">Submit & Continue</button>
          </div>
        </div>
      )}
    </div>
  );
}
